import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import { isIndividualChild } from "../../../constants/individualClub";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameCard, GameHero, GameIcon, StatTile, Skeleton } from "../../../components/game";
import {
  getParentChildren,
  getChildEventInvitations,
  confirmEventInvitation,
  declineEventInvitation,
  getEventNotes,
  type ParentChild,
  type EventInvitation,
  type EventNote,
} from "../../../services/api";

const invitationStatusColors: Record<string, string> = {
  invited: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
};

export default function AppParentEvents() {
  const { t } = useTranslation();
  const { activeChildId, setActiveChildId, setActiveChildIndividual } = useAuth();

  const invitationStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      invited: t("parent.invStatusInvited", "Invitat"),
      confirmed: t("parent.invStatusConfirmed", "Confirmat"),
      declined: t("parent.invStatusDeclined", "Refuzat"),
    };
    return labels[status] || status;
  };

  const [children, setChildren] = useState<ParentChild[]>([]);
  const [selectedChild, setSelectedChild] = useState<ParentChild | null>(null);
  const [loadingChildren, setLoadingChildren] = useState(true);

  const [invitations, setInvitations] = useState<EventInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [filter, setFilter] = useState<"all" | "invited" | "confirmed" | "declined">("all");

  // Event details modal
  const [selectedInvitation, setSelectedInvitation] = useState<EventInvitation | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState<EventNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchInvitations();
    }
    setActiveChildIndividual(isIndividualChild(selectedChild));
  }, [selectedChild]);

  const loadChildren = async () => {
    setLoadingChildren(true);
    try {
      const data = await getParentChildren();
      setChildren(data);
      if (data.length > 0) {
        const resolved = (activeChildId && data.find(c => c.athlete_id === activeChildId)) || data[0];
        setSelectedChild(resolved);
        setActiveChildId(resolved.athlete_id);
      }
    } catch (err) {
      console.error("Failed to load children:", err);
      toast.error(t("parent.childrenLoadError", "Nu s-au putut incarca datele copiilor"));
    }
    setLoadingChildren(false);
  };

  const fetchInvitations = async () => {
    if (!selectedChild) return;
    setLoadingInvitations(true);
    try {
      const data = await getChildEventInvitations(selectedChild.athlete_id);
      setInvitations(data.invitations);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
      toast.error(t("parent.invitationsLoadError", "Nu s-au putut incarca invitațiile"));
    }
    setLoadingInvitations(false);
  };

  const filteredInvitations = filter === "all"
    ? invitations
    : invitations.filter(inv => inv.status === filter);

  const openInvitationModal = async (invitation: EventInvitation) => {
    setSelectedInvitation(invitation);
    setShowModal(true);
    setDeclineReason("");

    if (invitation.status === "confirmed" && invitation.event) {
      loadNotes(invitation.event.id);
    }
  };

  const loadNotes = async (eventId: string) => {
    setLoadingNotes(true);
    try {
      const data = await getEventNotes(eventId);
      setNotes(data.notes);
    } catch (error) {
      console.error("Failed to load notes:", error);
    }
    setLoadingNotes(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInvitation(null);
    setNotes([]);
  };

  const handleConfirm = async () => {
    if (!selectedInvitation) return;

    setProcessing(true);
    try {
      await confirmEventInvitation(selectedInvitation.id);
      toast.success(t("parent.participationConfirmed", "Participare confirmată!"));
      await fetchInvitations();
      closeModal();
    } catch (error) {
      console.error("Failed to confirm:", error);
      toast.error(t("parent.participationConfirmError", "Nu s-a putut confirma participarea"));
    }
    setProcessing(false);
  };

  const handleDecline = async () => {
    if (!selectedInvitation) return;

    setProcessing(true);
    try {
      await declineEventInvitation(selectedInvitation.id, declineReason || undefined);
      toast.success(t("parent.invitationDeclined", "Invitație refuzata"));
      setShowDeclineModal(false);
      await fetchInvitations();
      closeModal();
    } catch (error) {
      console.error("Failed to decline:", error);
      toast.error(t("parent.invitationDeclineError", "Nu s-a putut refuza invitația"));
    }
    setProcessing(false);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getDaysUntil = (dateStr: string) => {
    const now = new Date();
    const diff = new Date(dateStr).getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return t("parent.dayPast", "Trecut");
    if (days === 0) return t("parent.dayToday", "Astăzi");
    if (days === 1) return t("parent.dayTomorrow", "Mâine");
    return t("parent.daysUntil", "in {{n}} zile", { n: days });
  };

  const pendingCount = invitations.filter(i => i.status === "invited").length;
  const confirmedCount = invitations.filter(i => i.status === "confirmed").length;
  const individual = isIndividualChild(selectedChild);

  if (loadingChildren) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6 space-y-4">
          <Skeleton className="h-16 rounded-2xl" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
        </div>
      </MobileAppLayout>
    );
  }

  if (children.length === 0) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6">
          <GameCard className="p-8 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="text-slate-500">{t("parent.noChildrenRegistered", "Nu aveți copii înregistrați")}</p>
          </GameCard>
        </div>
      </MobileAppLayout>
    );
  }

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header — shared dark hero, matching the athlete screens (no back
            arrow: this is a top-level screen reachable from the app nav). */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold truncate">{t("parent.events", "Evenimente")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5 truncate">
            {selectedChild ? selectedChild.athlete.full_name : t("parent.eventsSubtitle", "Competiții și activități")}
          </p>
        </GameHero>

      <div className="px-4 pt-5 space-y-6">
        {/* Child Selector */}
        {children.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 [&::-webkit-scrollbar]:hidden">
            {children.map(child => (
              <button
                key={child.athlete_id}
                onClick={() => { setSelectedChild(child); setActiveChildId(child.athlete_id); }}
                className={`px-4 py-2 text-sm font-bold rounded-full whitespace-nowrap transition-colors ${
                  selectedChild?.athlete_id === child.athlete_id
                    ? "bg-indigo-600 text-white shadow-[0_3px_0_#3730a3]"
                    : "bg-white text-slate-600 border border-slate-200"
                }`}
              >
                {child.athlete.full_name}
              </button>
            ))}
          </div>
        )}

        {individual ? (
          <GameCard className="p-6 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-blue-100 grid place-items-center">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-bold text-slate-800">{t("parent.noClubTitle", "Nu face parte dintr-un club")}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {t("parent.eventsNoClub", "Evenimentele apar aici după ce copilul se alătură unui club și este invitat de antrenor.")}
            </p>
          </GameCard>
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <StatTile icon="clock" color="#f59e0b" label={t("parent.newInvites", "Invitații noi")} value={pendingCount} />
          <StatTile icon="check" color="#10b981" label={t("parent.confirmed", "Confirmate")} value={confirmedCount} />
        </div>

        {/* Filter */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 [&::-webkit-scrollbar]:hidden">
          {[
            { value: "all", label: t("parent.filterAll", "Toate") },
            { value: "invited", label: t("parent.filterNew", "Noi") },
            { value: "confirmed", label: t("parent.filterConfirmed", "Confirmate") },
            { value: "declined", label: t("parent.filterDeclined", "Refuzate") },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value as any)}
              className={`px-4 py-2 text-sm font-bold rounded-full whitespace-nowrap transition-colors ${
                filter === opt.value
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Invitations List */}
        {loadingInvitations ? (
          <div className="space-y-3">
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
          </div>
        ) : filteredInvitations.length === 0 ? (
          <GameCard className="p-8 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-slate-500">
              {filter === "all"
                ? t("parent.noInvitations", "Nu există invitații la evenimente")
                : t("parent.noInvitationsFiltered", "Nu există invitații {{status}}", { status: invitationStatusLabel(filter).toLowerCase() })}
            </p>
          </GameCard>
        ) : (
          <div className="space-y-3">
            {filteredInvitations.map(invitation => (
              <GameCard
                key={invitation.id}
                depth
                onClick={() => openInvitationModal(invitation)}
                className="p-4 cursor-pointer active:translate-y-[2px] transition-transform"
              >
                <div className="flex gap-4">
                  {invitation.event?.photo_url ? (
                    <img
                      src={invitation.event.photo_url}
                      alt={invitation.event.title}
                      className="object-cover w-20 h-20 rounded-xl shrink-0"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-fuchsia-100 rounded-xl shrink-0">
                      <GameIcon name="star" className="w-8 h-8" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-extrabold text-slate-800 line-clamp-1">
                        {invitation.event?.title || t("parent.eventFallback", "Eveniment")}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full shrink-0 ${invitationStatusColors[invitation.status]}`}>
                        {invitationStatusLabel(invitation.status)}
                      </span>
                    </div>

                    {invitation.event && (
                      <>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="truncate">{formatDate(invitation.event.start_date)}{invitation.event.event_time && ` @ ${invitation.event.event_time}`}</span>
                        </div>

                        {invitation.event.location_city && (
                          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            <span className="truncate">{invitation.event.location_city}</span>
                          </div>
                        )}

                        <div className="mt-2">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full ${
                            new Date(invitation.event.start_date) > new Date()
                              ? "bg-indigo-50 text-indigo-600"
                              : "bg-slate-100 text-slate-500"
                          }`}>
                            {getDaysUntil(invitation.event.start_date)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </GameCard>
            ))}
          </div>
        )}
        </>
        )}
      </div>
      </div>

      {/* Event Details Modal */}
      {showModal && selectedInvitation && selectedInvitation.event && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-0 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-0"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg max-h-full sm:max-h-[90dvh] bg-white rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header. Only an event WITH a photo earns a tall banner — the
                placeholder gradient wasted 128px of a short modal, which is
                what made the header read as big and empty. */}
            <div className="relative shrink-0">
              {selectedInvitation.event.photo_url ? (
                <>
                  <img
                    src={selectedInvitation.event.photo_url}
                    alt={selectedInvitation.event.title}
                    className="object-cover w-full h-28"
                  />
                  <div className="absolute bottom-3 left-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${invitationStatusColors[selectedInvitation.status]}`}>
                      {invitationStatusLabel(selectedInvitation.status)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center w-full h-12 px-4 bg-gradient-to-r from-indigo-500 to-purple-500">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${invitationStatusColors[selectedInvitation.status]}`}>
                    {invitationStatusLabel(selectedInvitation.status)}
                  </span>
                </div>
              )}
              <button
                onClick={closeModal}
                className="absolute p-2 bg-white rounded-full shadow-lg top-2 right-3"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Info, notes and the decline reason share ONE scroll container.
                They used to be fixed rows, so a long description pushed the
                rest under the action buttons with no way to reach it. */}
            <div className="flex-1 overflow-y-auto overscroll-contain">

            {/* Event Info */}
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">{selectedInvitation.event.title}</h2>

              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>
                    {formatDate(selectedInvitation.event.start_date)}
                    {selectedInvitation.event.event_time && ` @ ${selectedInvitation.event.event_time}`}
                  </span>
                </div>

                {selectedInvitation.event.location_address && (
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <svg className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <span>
                      {selectedInvitation.event.location_address}
                      {selectedInvitation.event.location_city && `, ${selectedInvitation.event.location_city}`}
                    </span>
                  </div>
                )}

                {selectedInvitation.coach && (
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{t("parent.invitedBy", "Invitat de {{name}}", { name: selectedInvitation.coach.full_name || "—" })}</span>
                  </div>
                )}
              </div>

              {selectedInvitation.event.description && (
                <p className="mt-3 text-sm text-slate-600">{selectedInvitation.event.description}</p>
              )}

              {selectedInvitation.event.specifications && (
                <div className="p-3 mt-3 text-sm bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-700 mb-1">{t("parent.specifications", "Specificații:")}</p>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedInvitation.event.specifications}</p>
                </div>
              )}
            </div>

            {/* Notes (only for confirmed) */}
            {selectedInvitation.status === "confirmed" && (
              <div className="p-4">
                <h3 className="font-medium text-slate-700 mb-3">{t("parent.notes", "Note")}</h3>
                {loadingNotes ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="w-6 h-6 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin" />
                  </div>
                ) : notes.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-4">{t("parent.noNotes", "Nu există note")}</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => (
                      <div
                        key={note.id}
                        className={`p-3 rounded-lg ${
                          note.author_role === "club_admin"
                            ? "bg-blue-50 border-l-4 border-blue-500"
                            : "bg-emerald-50 border-l-4 border-emerald-500"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${
                            note.author_role === "club_admin" ? "text-blue-600" : "text-emerald-600"
                          }`}>
                            {note.author?.full_name || ""}
                            {note.author_role === "club_admin" && ` ${t("parent.adminSuffix", "(Admin)")}`}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(note.created_at).toLocaleString("ro-RO", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Decline reason info */}
            {selectedInvitation.status === "declined" && selectedInvitation.decline_reason && (
              <div className="p-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  <span className="font-medium">{t("parent.declineReasonLabel", "Motiv refuz:")}</span> {selectedInvitation.decline_reason}
                </p>
              </div>
            )}
            </div>

            {/* Action Buttons (only for pending invitations) — pinned below
                the scroll area so they are always reachable. */}
            {selectedInvitation.status === "invited" && (
              <div className="p-4 border-t border-slate-200 space-y-2 shrink-0">
                <button
                  onClick={handleConfirm}
                  disabled={processing}
                  className="w-full py-3 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
                >
                  {processing ? t("parent.processing", "Se proceseaza...") : t("parent.confirmParticipation", "Confirm participarea")}
                </button>
                <button
                  onClick={() => setShowDeclineModal(true)}
                  disabled={processing}
                  className="w-full py-3 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 disabled:opacity-50"
                >
                  {t("parent.cannotParticipate", "Nu poate participa")}
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Decline Reason Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDeclineModal(false)}>
          <div className="w-full max-w-sm bg-white rounded-2xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-4">{t("parent.cannotParticipateTitle", "Nu poate participa?")}</h3>
            <p className="text-sm text-slate-600 mb-3">{t("parent.addReasonOptional", "Puteți adauga un motiv (opțional):")}</p>
            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder={t("parent.declineReasonPlaceholder", "Ex: Are un alt eveniment in acea zi...")}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="flex-1 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                {t("parent.cancel", "Anulează")}
              </button>
              <button
                onClick={handleDecline}
                disabled={processing}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? "..." : t("parent.declineInvitation", "Refuza invitație")}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileAppLayout>
  );
}
